/**
 * Offline Camera Service
 * Handles photo capture with GPS tagging, offline storage, and background sync
 * Works completely offline and syncs when connection is restored
 */

import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import indexedDBHelper from '../utils/indexedDBHelper';
import { sha256Binary, base64ToArrayBuffer } from '../utils/integrityChain';
import api from './api';
const axios = api;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class OfflineCameraService {
  constructor() {
    this.syncInProgress = false;
    this.syncListeners = [];
  }

  /**
   * Check if running on native platform (Android/iOS)
   */
  isNativePlatform() {
    return Capacitor.isNativePlatform();
  }

  /**
   * Get current GPS location with high accuracy
   * CRITICAL: This must succeed before photo capture
   */
  async getCurrentLocation() {
    try {
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0 // Force fresh GPS reading
      });

      return {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        altitude: position.coords.altitude,
        heading: position.coords.heading,
        speed: position.coords.speed,
        timestamp: position.timestamp
      };
    } catch (error) {
      console.error('GPS Error:', error);
      throw new Error(`GPS lock failed: ${error.message}. Please ensure GPS is enabled and try again in an open area.`);
    }
  }

  /**
   * Validate GPS accuracy (must be reasonable for construction verification)
   */
  validateGPSAccuracy(location) {
    const MAX_ACCURACY = 100; // meters

    if (!location || !location.latitude || !location.longitude) {
      return {
        valid: false,
        message: 'GPS coordinates missing'
      };
    }

    if (location.accuracy > MAX_ACCURACY) {
      return {
        valid: false,
        message: `GPS accuracy too low (${Math.round(location.accuracy)}m). Maximum: ${MAX_ACCURACY}m. Move to an open area.`,
        accuracy: location.accuracy
      };
    }

    return {
      valid: true,
      message: `GPS locked (${Math.round(location.accuracy)}m accuracy)`,
      accuracy: location.accuracy,
      quality: location.accuracy < 30 ? 'excellent' : location.accuracy < 60 ? 'good' : 'acceptable'
    };
  }

  /**
   * Get device information for anti-spoofing
   */
  getDeviceInfo() {
    return {
      platform: Capacitor.getPlatform(),
      isNative: this.isNativePlatform(),
      userAgent: navigator.userAgent,
      timestamp: Date.now()
    };
  }

  /**
   * Capture photo with GPS tagging (works offline)
   * STEP 1: Get GPS lock BEFORE taking photo
   * STEP 2: Capture photo
   * STEP 3: Store locally with metadata
   * STEP 4: Upload if online (or queue for later)
   */
  async capturePhoto(options = {}) {
    try {
      // STEP 1: Get GPS lock BEFORE photo (CRITICAL)
      const location = await this.getCurrentLocation();

      // Validate GPS accuracy
      const gpsValidation = this.validateGPSAccuracy(location);
      if (!gpsValidation.valid) {
        throw new Error(gpsValidation.message);
      }

      // STEP 2: Capture photo with native camera
      const photo = await Camera.getPhoto({
        quality: 90,
        resultType: CameraResultType.Base64,
        source: CameraSource.Camera,
        saveToGallery: false, // We manage storage ourselves
        correctOrientation: true,
        allowEditing: false
      });

      // STEP 3: Compute SHA-256 hash of photo binary for integrity chain
      let contentHash = null;
      try {
        const buffer = base64ToArrayBuffer(photo.base64String);
        contentHash = await sha256Binary(buffer);
      } catch (hashErr) {
        console.warn('Photo hash computation failed, continuing without:', hashErr);
      }

      // STEP 4: Create photo data with metadata
      const photoData = {
        id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        base64: photo.base64String,
        format: photo.format || 'jpeg',
        location: location,
        gpsQuality: gpsValidation.quality,
        deviceInfo: this.getDeviceInfo(),
        uploaded: false,
        timestamp: Date.now(),
        capturedAt: new Date().toISOString(),
        verificationId: options.verificationId || null,
        projectId: options.projectId || null,
        sessionId: options.sessionId || null,
        checklistItemId: options.checklistItemId || null,
        caption: options.caption || null,
        contentHash: contentHash,
        metadata: {
          offlineCapture: true,
          networkStatus: navigator.onLine ? 'online' : 'offline'
        }
      };

      // STEP 5: Store in IndexedDB
      await indexedDBHelper.storePhoto(photoData);

      // STEP 6: Try to upload if online (skip if part of offline session)
      if (navigator.onLine && !options.sessionId) {
        // Don't await - let it happen in background
        // Skip auto-upload if photo belongs to an offline session (sync handles it)
        this.uploadPhoto(photoData.id).catch(error => {
          console.warn('Background upload failed, will retry later:', error);
        });
      }

      return {
        success: true,
        photoId: photoData.id,
        photo: photoData,
        message: navigator.onLine
          ? 'Photo captured and uploading...'
          : 'Photo captured offline. Will sync when online.',
        gpsQuality: gpsValidation.quality
      };

    } catch (error) {
      console.error('Photo capture error:', error);
      throw error;
    }
  }

  /**
   * Upload a single photo to server
   * @param {string} photoId - ID of photo in IndexedDB
   */
  async uploadPhoto(photoId) {
    try {
      // Get photo from IndexedDB
      const photo = await indexedDBHelper.getPhoto(photoId);

      if (!photo) {
        throw new Error('Photo not found in local storage');
      }

      if (photo.uploaded) {
        return { success: true, message: 'Photo already uploaded' };
      }

      // Convert base64 to blob for upload
      const base64Data = photo.base64;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: `image/${photo.format}` });

      // Create FormData
      const formData = new FormData();
      formData.append('images', blob, `${photo.id}.${photo.format}`);
      formData.append('metadata', JSON.stringify({
        location: photo.location,
        gpsQuality: photo.gpsQuality,
        deviceInfo: photo.deviceInfo,
        capturedAt: photo.capturedAt,
        offlineCapture: true
      }));

      if (photo.verificationId) {
        formData.append('verificationId', photo.verificationId);
      }
      if (photo.projectId) {
        formData.append('projectId', photo.projectId);
      }
      if (photo.caption) {
        formData.append('caption', photo.caption);
      }

      // Upload to server
      const response = await axios.post(
        `${API_BASE_URL}/uploads/verification-photos`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      // Mark as uploaded in IndexedDB
      await indexedDBHelper.updatePhoto(photoId, {
        uploaded: true,
        uploadedAt: Date.now(),
        serverUrl: response.data.data?.url || null,
        serverId: response.data.data?.id || null
      });

      return {
        success: true,
        data: response.data.data,
        message: 'Photo uploaded successfully'
      };

    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  }

  /**
   * Sync all pending photos (called when connection is restored)
   */
  async syncPendingPhotos() {
    if (this.syncInProgress) {
      return { success: false, message: 'Sync already in progress' };
    }

    this.syncInProgress = true;
    this.notifySyncListeners({ status: 'started' });

    try {
      // Get all pending photos
      const pendingPhotos = await indexedDBHelper.getAllPhotos({ uploaded: false });

      if (pendingPhotos.length === 0) {
        this.syncInProgress = false;
        this.notifySyncListeners({ status: 'completed', uploaded: 0, failed: 0 });
        return {
          success: true,
          message: 'No pending photos to sync',
          uploaded: 0,
          failed: 0
        };
      }

      let uploaded = 0;
      let failed = 0;

      // Upload photos one by one
      for (const photo of pendingPhotos) {
        try {
          await this.uploadPhoto(photo.id);
          uploaded++;
          this.notifySyncListeners({
            status: 'progress',
            uploaded,
            failed,
            total: pendingPhotos.length
          });
        } catch (error) {
          console.error(`Failed to upload photo ${photo.id}:`, error);
          failed++;
        }
      }

      this.syncInProgress = false;
      this.notifySyncListeners({ status: 'completed', uploaded, failed });

      return {
        success: true,
        message: `Sync completed: ${uploaded} uploaded, ${failed} failed`,
        uploaded,
        failed,
        total: pendingPhotos.length
      };

    } catch (error) {
      this.syncInProgress = false;
      this.notifySyncListeners({ status: 'error', error: error.message });
      throw error;
    }
  }

  /**
   * Get all offline photos for a verification
   */
  async getOfflinePhotos(verificationId = null) {
    const filters = verificationId ? { verificationId } : {};
    return await indexedDBHelper.getAllPhotos(filters);
  }

  /**
   * Get pending upload count
   */
  async getPendingCount() {
    return await indexedDBHelper.getPendingUploadCount();
  }

  /**
   * Delete a photo
   */
  async deletePhoto(photoId) {
    return await indexedDBHelper.deletePhoto(photoId);
  }

  /**
   * Get storage statistics
   */
  async getStorageStats() {
    const allPhotos = await indexedDBHelper.getAllPhotos();
    const pendingPhotos = await indexedDBHelper.getAllPhotos({ uploaded: false });
    const storageEstimate = await indexedDBHelper.getStorageEstimate();

    return {
      totalPhotos: allPhotos.length,
      pendingUpload: pendingPhotos.length,
      uploaded: allPhotos.length - pendingPhotos.length,
      storage: storageEstimate
    };
  }

  /**
   * Register listener for sync events
   */
  onSyncStatusChange(callback) {
    this.syncListeners.push(callback);
    return () => {
      this.syncListeners = this.syncListeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all sync listeners
   */
  notifySyncListeners(status) {
    this.syncListeners.forEach(callback => {
      try {
        callback(status);
      } catch (error) {
        console.error('Sync listener error:', error);
      }
    });
  }

  /**
   * Setup automatic sync when connection is restored
   */
  setupAutoSync() {
    if (!this.autoSyncEnabled) {
      window.addEventListener('online', () => {
        console.log('Connection restored, starting auto-sync...');
        this.syncPendingPhotos().catch(error => {
          console.error('Auto-sync failed:', error);
        });
      });
      this.autoSyncEnabled = true;
    }
  }
}

// Export singleton instance
const offlineCameraService = new OfflineCameraService();

// Setup auto-sync on module load
if (typeof window !== 'undefined') {
  offlineCameraService.setupAutoSync();
}

export default offlineCameraService;
